import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../reducers';
import { Button } from '../components/ui/button';
import Loader from '../features/app/loader/LoaderFullScreen';
import { getInitialState } from '../features/app/appSlice';
import {
  getToken,
  setToken,
  isTokenExpired,
  removeToken,
  isNewerThanStoredToken,
} from '../lib/localStorageHandler';
import { setHeader } from '../lib/baseAxios';
import { addNotification } from '../features/dashboard/dashboardSlice';
import { useQuery } from '../hooks/urlHooks';
import AccountDeletingCover from '../features/auth/AccountDeletingCover';
import { getParameterFromURLHash } from '../utils/secure';
import URLDialogManager from '../features/app/URLDialogManager';
import {
  sanitizeHashPath,
  toSafeRedirectHashPath,
} from '../lib/authRedirectUtils';

const PrivateRoute: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: tokenFromQuery,
    emailVerification,
    joinCode,
    zoomCode: zoomCodeFromQuery,
    state: stateFromQuery,
    redirectUrl,
    intent,
  } = useQuery();
  const zoomCode = zoomCodeFromQuery || getParameterFromURLHash('code');

  const stateValue = stateFromQuery || getParameterFromURLHash('state');

  const isEmailVerification = !!emailVerification;
  const tokenFromLocalStorage = getToken();
  const token = tokenFromQuery || tokenFromLocalStorage;
  const { app } = useSelector((state: RootState) => state) || {};
  const { authFailed, errorMessage, status: appStatus } = app;
  const deleteRequestedAt = useSelector(
    (state: RootState) => state.settings.user?.deleteRequestedAt,
  );
  const dispatch = useDispatch();
  const currentHashPath = sanitizeHashPath(location.pathname, location.search);
  const lastBootstrappedTokenRef = useRef<string | null>(null);
  const shouldAllowInitialBootstrapForQueryToken = Boolean(
    tokenFromQuery && lastBootstrappedTokenRef.current !== tokenFromQuery,
  );

  const getAuthRedirectParams = () => {
    const currentLocationBase = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    const redirectTarget = `${currentLocationBase}#${currentHashPath}`;
    const queryParams = new URLSearchParams({
      redirectUrl: redirectTarget,
      referrerUrl: currentHashPath,
    });

    if (intent) {
      queryParams.set('intent', intent);
    }

    return queryParams;
  };

  const retryInitialLoad = () => {
    if (!token) {
      return;
    }

    setHeader(token);
    dispatch(
      getInitialState({
        joinCode,
        zoomCode,
        state: stateValue,
      }),
    );
  };

  const signOutAndRedirect = () => {
    removeToken();
    navigate('/signin', { replace: true });
  };

  useEffect(() => {
    // Set token from query only if it's newer than the stored one
    if (tokenFromQuery && isNewerThanStoredToken(tokenFromQuery)) {
      setToken(tokenFromQuery);
    }

    if (!token) {
      return;
    }

    const shouldBootstrapForToken =
      lastBootstrappedTokenRef.current !== token ||
      appStatus === 'idle' ||
      (appStatus === 'failed' && shouldAllowInitialBootstrapForQueryToken);

    if (!shouldBootstrapForToken) {
      return;
    }

    lastBootstrappedTokenRef.current = token;
    setHeader(token);

    // Only dispatch getInitialState if `joinCode` or `zoomCode` is provided
    if (joinCode || zoomCode) {
      dispatch(
        getInitialState({
          joinCode,
          zoomCode,
          state: stateValue,
        }),
      );
    } else {
      dispatch(getInitialState({}));
    }

    if (isEmailVerification) {
      dispatch(
        addNotification({
          id: Date.now().toString(),
          type: 'transcription',
          title: t('authenticatedRoute.emailVerified.title'),
          message: t('authenticatedRoute.emailVerified.message'),
          link: '',
          read: false,
          timestamp: Date.now(),
        }),
      );
    }

    // Redirect based on joinCode or zoomCode
    if (joinCode) {
      navigate(`/dashboard?joinCode=${joinCode}`);
    } else if (zoomCode) {
      navigate(`/dashboard?code=${zoomCode}`);
    }
  }, [
    tokenFromQuery,
    appStatus,
    token,
    joinCode,
    zoomCode,
    isEmailVerification,
    dispatch,
    navigate,
    t,
    stateValue,
    shouldAllowInitialBootstrapForQueryToken,
  ]);

  if (!token) {
    const queryParams = getAuthRedirectParams();
    return <Navigate to={`/signup?${queryParams}`} replace />;
  }

  // Check if token is expired and remove it if so
  if (isTokenExpired(token)) {
    removeToken();
    const queryParams = getAuthRedirectParams();
    return <Navigate to={`/signin?${queryParams}`} replace />;
  }

  if (
    appStatus === 'failed' &&
    authFailed &&
    !shouldAllowInitialBootstrapForQueryToken
  ) {
    const queryParams = getAuthRedirectParams();
    return <Navigate to={`/signin?${queryParams}`} replace />;
  }

  if (deleteRequestedAt) {
    return <AccountDeletingCover />;
  }

  const redirectHashPath = toSafeRedirectHashPath(redirectUrl, currentHashPath);
  if (appStatus === 'succeeded' && redirectHashPath) {
    return <Navigate to={redirectHashPath} replace />;
  }

  if (appStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="w-full max-w-md rounded-2xl border bg-card p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-foreground">
            {t('errors.generic.title', 'Something went wrong')}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {errorMessage ||
              t('errors.generic', 'Something went wrong. Please try again.')}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button onClick={retryInitialLoad}>
              {t('common.retry', 'Retry')}
            </Button>
            <Button variant="outline" onClick={signOutAndRedirect}>
              {t('settings.logout', 'Logout')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (appStatus !== 'succeeded') return <Loader />;

  return (
    <>
      <Outlet />
      <URLDialogManager />
    </>
  );
};

export default PrivateRoute;
